import { useConfirmation } from '@/hooks/useConfirmation';
import { useEffect, type ReactElement, type ReactNode } from 'react';
import { useSubmissionWorkspace } from '@/hooks/useSubmissionWorkspace';
import { Link, useLocation, useNavigate } from 'react-router-dom';

interface Props {
  submissionId: string;
  name: string;
  sequence: string | null | undefined;
  active: 'details' | 'compilation' | 'tool';
  header?: ReactNode;
  warnings?: ReactNode;
  actions?: ReactNode;
  hasUnsavedChanges?: boolean;
}

/** Navigation only: existing pages and server-side workflow gates remain authoritative. */
export function SubmissionWorkspaceTabs({ submissionId, name, sequence, active, header, warnings, actions, hasUnsavedChanges = false }: Props): ReactElement {
  const { ask, dialog } = useConfirmation();
  const navigate = useNavigate();
  const workspace = useSubmissionWorkspace();
  const location = useLocation();
  const url = location.pathname + location.search + location.hash;
  useEffect(() => {
    workspace?.register({ id: submissionId, name, sequence, url });
  }, [workspace, submissionId, name, sequence, url]);
  useEffect(() => {
    if (workspace) workspace.dirty.current = hasUnsavedChanges;
    return () => { if (workspace) workspace.dirty.current = false; };
  }, [workspace, hasUnsavedChanges, url]);
  const base = `/submissions/${encodeURIComponent(submissionId)}`;
  return (
    <>
    {dialog}
    <div className="submission-workspace-tabs">
      {header ?? <div className="submission-workspace-context"><strong>{name} · Sequence {sequence ?? '—'}</strong></div>}
      <div className="submission-workspace-navigation">
      <nav aria-label="Submission workspace">
        {([
          { key: 'details', label: 'Submission Details', to: base },
          { key: 'compilation', label: 'Submission Compilation', to: `${base}/compiler` },
        ] as const).map((tab) => {
          return (
            <Link key={tab.key} to={tab.to} className="submission-workspace-tab" aria-current={active === tab.key ? 'page' : undefined}
              onClick={(event) => {
                if (active === tab.key) { event.preventDefault(); return; }
                if (!workspace && hasUnsavedChanges) {
                  event.preventDefault();
                  const href = event.currentTarget.getAttribute('href');
                  void ask({ title: 'Unsaved changes', message: 'You have unsaved changes. Leave this page and discard them?', confirmLabel: 'Discard and leave', variant: 'destructive' }).then((accepted) => { if (accepted && href) navigate(href); });
                }
              }}>
              {tab.label}
            </Link>
          );
        })}
      </nav>
      {actions}
      <Link to={`/submissions/new?supplementOf=${encodeURIComponent(submissionId)}`}
        className="btn btn-primary submission-workspace-supplement" data-testid="new-supplement-link"
        onClick={(event) => {
          if (!workspace && hasUnsavedChanges) {
                  event.preventDefault();
                  const href = event.currentTarget.getAttribute('href');
                  void ask({ title: 'Unsaved changes', message: 'You have unsaved changes. Leave this page and discard them?', confirmLabel: 'Discard and leave', variant: 'destructive' }).then((accepted) => { if (accepted && href) navigate(href); });
                }
        }}>
        + New Supplement
      </Link>
      </div>
      {warnings}
    </div>
    </>
  );
}
